import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import FurtherAction from '@components/individualPortal/dashboard/FurtherAction';
import runStateMachine from '@utils/stateMachineRules/runStateMachine';
import { dateMethods } from '@utils/dates/dateMethods';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { ctaHandlers } from '@utils/dashboard/stateSpecificUtils';
import { processCommon } from '@utils/dashboard/processCommon';
import {
    FURTHER_ACTION_LINKS,
    FORMATTED_DATES,
} from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { showModal } from '@actions/common/actionCreators';
import {
    setCTA,
    checkActiveEnrollment,
    cloneApplication,
    changeAppStatus,
    checkPriorSubmittedApp,
} from '@actions/individualPortal/dashboard/dashboardActionCreators';
import { configs } from '@constants/configs/configs';
import { useExchangeConditions } from '@hooks/index';
import { ConditionalRendering } from '@components/common/conditional-rendering';

const FurtherActionContainer = props => {
    const {
        environment: {
            language,
            stateCode,
            user,
            links: { PHONE },
            ssapFlowVersion,
            tobaccoEnabled,
            hardshipEnabled,
            localDates,
            localDates: { serverDate, oepStartDate, oepEndDate },
            flags,
            lowFplOnceAMonthShoppingThreshold,
            isShoppingOpenForEntireShoppingWindow,
            isState,
            isMNFullStack,
            isStateFullStack,
            cmrHouseholdId,
            impersonation,
            impersonationDetails,
            exchangeName,
        },
        dashboardModels,
        actions,
        activeTab,
    } = props;
    sessionStorage.setItem('isMNFullStack', isMNFullStack);
    const { shouldRedirectToCustomGrouping } = useExchangeConditions();
    const { getFormattedDate } = dateMethods;
    const {
        isEnrolledInDental,
        isEnrolledInHealth,
        isAllEnrollmentCreationDateWithinThisMonth,
        getEnrollmentReplacements,
    } = processCommon;
    const {
        goToStartApp,
        goToResumeApp,
        goToContactUs,
        goToContinue,
        goToQLEVerifications,
        goToReportAChange,
        goToConfirmEvent,
        goToGroupingScreen,
    } = ctaHandlers;
    const activeTabData = dashboardModels[activeTab];
    const {
        caseNumber,
        applicationStatus,
        applicationYear,
        isInsideOEWindow,
        isFinancial,
        isLocked,
        enrollments,
        ssapApplicationId,
        encryptedCaseNumber,
        encryptedApplicationId,
        applicants,
    } = activeTabData;

    const isTobaccoInformationAvailableForAllUser = !applicants?.some(
        applicant => [null, undefined, 'undefined'].includes(applicant.tobacco),
    );

    const CURRENT_YEAR = getFormattedDate(FORMATTED_DATES.currentYear, applicationYear);
    const NEXT_YEAR = getFormattedDate(FORMATTED_DATES.nextYear, applicationYear, isInsideOEWindow);
    const PREVIOUS_YEAR = getFormattedDate(FORMATTED_DATES.previousYear, applicationYear);

    const furtherActionMessages = messageId => {
        // Temp NJ override for ARPA 2021 tab only. Will be removed later.
        if (isState.NJ && messageId === 'further_action.further_text.OE5' && applicationYear === '2021') {
            messageId = 'further_action.further_text.OE5_ARPA';
        }

        const hasDental = isEnrolledInDental(enrollments, serverDate);
        const hasHealth = isEnrolledInHealth(enrollments);
        const enrollmentExists = hasHealth || hasDental;
        const enrollmentsValues = getEnrollmentReplacements(hasHealth, hasDental);


        return (
            <div className='grid-row'>
                <div style={{ flex: '1 1 auto' }}>
                    <FormattedMessage
                        id={messageId.props ? messageId.props.id : messageId}
                        values={{
                            strong: chunks => <strong>{chunks}</strong>,
                            space: <span>&nbsp;</span>,
                            oepStartDate: getFormattedDate(FORMATTED_DATES.oepStartDate, oepStartDate),
                            oepEndDate: getFormattedDate(FORMATTED_DATES.oepEndDate, oepEndDate),
                            previousYear: PREVIOUS_YEAR,
                            upcomingYear: NEXT_YEAR,
                            currentYear: CURRENT_YEAR,
                            renewalsUpcomingYear: CURRENT_YEAR,
                            isFinancial: isFinancial ? (
                                <FormattedMessage id="further_action.coverage_status.and_financial" />
                            ) : (
                                ''
                            ),
                            QLElink: <FormattedMessage id="further_action.coverage_status.qualifying_life_event" />,
                            enrolledInHealthDental: enrollmentExists && (
                                <FormattedMessage id="further_action.further_text_health_dental" values={enrollmentsValues} />
                            ),
                            enrolledInHealthDentalSuccess: enrollmentExists && (
                                <FormattedMessage
                                    id="further_action.further_text_health_dental_success"
                                    values={enrollmentsValues}
                                />
                            ),
                            changePlansAction: (
                                <>
                                    <ConditionalRendering isTrue={hasDental}>
                                        <FormattedMessage id="further_action.further_text.SEP6_action_health_and_dental" />
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!hasDental}>
                                        <FormattedMessage id="further_action.further_text.SEP6_action_health_only" />
                                    </ConditionalRendering>
                                </>
                            ),
                            importantAction: <FormattedMessage id="further_action.action_needed" tagName="b" />,
                            eligibilityLink: (
                                <>
                                    &nbsp;
                                    <Link
                                        to={configs.routes.ELIGIBILITY_RESULTS}
                                        state={{
                                            prevLocation: configs.routes.INDPORTAL,
                                            encryptedCaseNumber: encryptedCaseNumber,
                                        }}
                                    >
                                        <FormattedMessage id="common.here" />
                                    </Link>
                                </>
                            ),
                            phone: PHONE,
                            lineBreak: <br />,
                            exchangeName,
                        }}
                    />
                </div>
            </div>
        );
    };

    const getFurtherActionData = () => {
        const defaultRoleName = (user && user.role) || 'INDIVIDUAL';
        const model = { ...activeTabData, defaultRoleName };
        const smFurtherActionsRes = runStateMachine(
            model,
            {
                ...localDates,
                flags,
                isLocked,
                isShoppingOpenForEntireShoppingWindow,
                isStateFullStack,
                lowFplOnceAMonthShoppingThreshold,
            },
            stateCode,
        );

        const btnText = smFurtherActionsRes.btnText;
        const processedParagraphText = furtherActionMessages(smFurtherActionsRes.paragraphText);

        return {
            ...smFurtherActionsRes,
            btnText,
            processedParagraphText,
        };
    };

    const checkIfEnrollmentChanged = () => {
        if (isAllEnrollmentCreationDateWithinThisMonth(activeTabData.enrollments, serverDate)) {
            actions.showModal({
                modalType: dashboardModalTypes.CHANGE_PLANS_MODAL,
            });
        } else {
            goToGroupingScreen({
                tobaccoEnabled,
                hardshipEnabled,
                encryptedCaseNumber,
                applicationStatus,
                isTobaccoInformationAvailableForAllUser,
                shouldRedirectToCustomGrouping,
            });
        }
    };

    const furtherActionCTA = link => {
        actions.setCTA(link);
        const ssapParams = {
            ssapFlowVersion,
            caseNumber,
            applicationStatus,
            applicationYear,
            isInsideOEWindow,
            isFinancial,
            ssapApplicationId,
            encryptedApplicationId,
            cmrHouseholdId: impersonation && impersonationDetails ? impersonationDetails.householdId : cmrHouseholdId,
        };

        switch (link) {
            case FURTHER_ACTION_LINKS.START_NEW_APPLICATION:
                goToStartApp(ssapParams, actions, isStateFullStack);
                break;

            case FURTHER_ACTION_LINKS.RESUME_APPLICATION:
                goToResumeApp(stateCode, ssapParams, isStateFullStack);
                break;

            case FURTHER_ACTION_LINKS.REPORT_CHANGE:
            case FURTHER_ACTION_LINKS.REPORT_LIFE_EVENT_SHOP:
                goToReportAChange(stateCode, ssapParams, actions, isStateFullStack);
                break;

            case FURTHER_ACTION_LINKS.GROUPING_SCREEN:
                goToGroupingScreen({
                    tobaccoEnabled,
                    hardshipEnabled,
                    encryptedCaseNumber,
                    applicationStatus,
                    isTobaccoInformationAvailableForAllUser,
                    shouldRedirectToCustomGrouping,
                });
                break;

            case FURTHER_ACTION_LINKS.GROUPING_SCREEN_IF_ALL_ENROLLMENTS_NOT_WITHIN_THIS_MONTH:
                checkIfEnrollmentChanged();
                break;

            case FURTHER_ACTION_LINKS.CONFIRM_EVENT: {
                goToConfirmEvent(activeTabData.encryptedCaseNumber);
                break;
            }

            case FURTHER_ACTION_LINKS.UPLOAD_DOCUMENTS:
                goToQLEVerifications(caseNumber, language, stateCode, isMNFullStack);
                break;

            case FURTHER_ACTION_LINKS.CONTINUE:
                goToContinue(stateCode, isStateFullStack);
                break;

            case FURTHER_ACTION_LINKS.CONTACT_US:
                goToContactUs(stateCode, isStateFullStack);
                break;

            default:
                break;
        }
    };

    const furtherActionData = getFurtherActionData();

    return <FurtherAction data={furtherActionData} onCTAHandler={() => furtherActionCTA(furtherActionData.link)} />;
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    dashboardModels: state.individualPortal.dashboard.dashboardModels,
    activeTab: state.individualPortal.dashboard.activeTab,
    ctaType: state.individualPortal.dashboard.ctaType,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            setCTA,
            showModal,
            checkActiveEnrollment,
            cloneApplication,
            changeAppStatus,
            checkPriorSubmittedApp,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(FurtherActionContainer);
